/**
 * Tenant off-boarding — lifecycle Phase D (17 Jul).
 *
 * Superadmin deletes a CUSTOMER: download their full backup first (the same
 * multi-sheet Excel the per-customer backup button produces), type the
 * company name, and the tenant enters a 30-DAY GRACE — everyone blocked
 * immediately, undoable via Activate, purged permanently by the overnight
 * scheduler after day 30 (cascades take everything; every email freed).
 */
import React, { useState } from 'react';
import { AlertTriangle, Download, Loader2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { superadminApi } from '../lib/api';
import { toast } from 'sonner';

export default function DeleteTenantDialog({ open, onOpenChange, tenant, onSuccess }) {
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);

  if (!tenant) return null;
  const nameMatches = confirmName.trim().toLowerCase() === (tenant.company_name || '').trim().toLowerCase();

  const download = async () => {
    try {
      const r = await superadminApi.exportTenant(tenant.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(tenant.company_name || 'company').replace(/\s+/g, '-')}-full-backup.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not export their data');
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await superadminApi.actionTenant(tenant.id, 'delete');
      toast.success(r.data?.message || 'Deletion scheduled — 30-day grace started');
      onOpenChange(false);
      setConfirmName('');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setConfirmName(''); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-red-500">
            Delete customer: {tenant.company_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">30-day grace, then gone forever.</p>
              <p className="text-muted-foreground text-xs mt-1">
                All {tenant.user_count} user(s) are blocked immediately. "Activate"
                undoes this any time in the next 30 days. After that the overnight
                job permanently deletes everything — people, bookings, balances —
                and frees every email address in the company.
              </p>
            </div>
          </div>

          <Button type="button" variant="outline" onClick={download} className="w-full" data-testid="tenant-delete-export">
            <Download className="w-4 h-4 mr-2" />
            Download their full backup first
          </Button>

          <div>
            <Label>Type the company name to confirm: <span className="font-semibold">{tenant.company_name}</span></Label>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                   className="input-hive mt-1" placeholder={tenant.company_name}
                   data-testid="tenant-delete-confirm" />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={confirm} disabled={busy || !nameMatches}
                    data-testid="tenant-delete-go">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</>)
                : (<><Trash2 className="w-4 h-4 mr-2" />Start 30-day deletion</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
